import { type Cart as ProtoCart, type CartHeader, type Customer, type ItemAndMenu, type patchDelivery, DeliveryOption } from "$lib/def/food-cart";
import type { AddonGroup, ExtraGroup, Item, Menu, OptionGroup } from "proto/menu";
import type { CartItem, CartPayload, ChoiceGroup, IDed, Quote, WithOptionsAndExtras } from "$lib/def/food-menu";
import { LOCATION_PROPS, type LandingItemsLocation } from "./locations";
import type { Address, GeoPoint } from "proto/address";
import type { Order } from "proto/order";
import type { User } from "proto/user";

import { BehaviorSubject, EMPTY, Observable, Subject, catchError, map, merge, of, share, switchMap, tap, withLatestFrom, firstValueFrom, filter, debounceTime, combineLatest, type ValueFromArray, throwError, distinctUntilChanged, shareReplay, timer, take } from "rxjs";
import { deleteAnon$, get$, post$, getAnon$, patchAnon$, postAnon$, putAnon$ } from "./core/auth";
import { allKeysStartingWith, getItem, observe, setItem } from "./core/local-store";
import { Modes, create, each, enforce, mode, omitWhen, test } from "vest";
import { cleanAddress, hasGeo } from "$lib/util/address";
import { AlertType, toast, toastInfo } from "./toast";
import { objectPropsEqual } from "$lib/util/object";
import { Vertical } from "$lib/def/verticals";
import { RxWritable, fromStore } from "$lib/util/obs";
import { capitalizeFirstLetter } from "$lib/util/format";

const KEY_HEADER = 'food-cart-header';

// TODO: @Dan - remove when protos are updated
interface Cart extends ProtoCart {
	vertical?: Vertical
}

// ######################################################################################
//
// CART-HEADER VS CART
//
// A cart header is the bridge between a customer's interest in a restaurant and an
// actual server-side cart. These are stored with keys `food-cart-header-[restaurantId]`.
//
// ** Local indexDB carts have now been removed (may be added again later) **
// A cart is a copy of a server-side cart and stored with keys `food-cart-[cart-id]`.
// Locally stored carts are NEVER modified directly. They are always only modified when
// an updated cart is received from the server and then set as the updated cart
// client-side. **It is simply to improve responsiveness when a user refreshes.**
//
// General flow of cart modification:
// Customer chooses a restaurant item -> look up restaurant cart header -> API request
// cart update (-> update local cart, removed for now) -> display change to customer.
//
// ######################################################################################

/**
 * Updated every time UI cart emits
 * Manipulated for immediate UI response and updated after each successful API call
 */
const cartSubject$: Subject<Cart> = new Subject();

/**
 * Set from +layout.svelte
 */
const profileStream = new BehaviorSubject<User | null>(null);
export const updateProfile = (user: User | null) => profileStream.next(user);

/**
 * Set from +layout.svelte
 */
const addressStream = new BehaviorSubject<LandingItemsLocation>({}); // BehaviorSubject doesn't get set twice - leaving address blank
export const updateAddress = (address: LandingItemsLocation) => addressStream.next(address);
		
/**
 * Should emit on page load
 */
const initCart$: Observable<Cart | null> = combineLatest([profileStream, addressStream.pipe(
	// Getting emits from any url change - should only emit on address change
	distinctUntilChanged(objectPropsEqual(...LOCATION_PROPS, 'vertical')),
)]).pipe(
	switchMap(([auth, address]) => {

		const latest = getLatestCartHeader(address);

		// auth cart first, then last edited local cart
		if (auth) return get$<Cart>(`ca://restaurant/cart`).pipe( // override local cart if both exist
			switchMap((cart) => {
				if (
					(cart.updated_at_ts || 0) >= (latest?.lastUpdated || 0) &&
					(cart.vertical ?? Vertical.Restaurant) === (address.vertical ?? Vertical.Restaurant) &&
					!!cart.customer.address && // prevents old headers without address
					cart.customer.address.geo_location?.lat === +(address.latitude || 0) &&
					cart.customer.address.geo_location?.lon === +(address.longitude || 0)
				) return of(cart);
				if (latest && (latest.expiresAt > Date.now())) return getAnon$<Cart>(`ca://restaurant/cart/${latest.id}`);
				return of(null);
			}),
			catchError((e) => { // 500 - no cart from auth
				// console.log('No cart from authed call');
				if (latest) return getAnon$<Cart>(`ca://restaurant/cart/${latest.id}`);
				return of(null);
			})
		);

		// not logged in, has a localStorage cart header
		if (latest) return getAnon$<Cart>(`ca://restaurant/cart/${latest.id}`);
		
		// not logged in, no localStorage cart headers
		return of(null);
	}),
	// removed according to FEW-752
	// map((cart) => {
	// 	if (cart) {
	// 		if (_cartIsValid(cart)) return cart;
	// 		const keyh = formatCartHeader(cart.restaurant.id, cart.customer.address.geo_location);
	// 		setItem(keyh); // delete local copy
	// 	}
	// 	return null;
	// }),
	catchError((e) => { // no cart from auth or local storage headers
		// setToastMessage(e?.friendly_message, AlertType.Error);
		console.log(e?.friendly_message ?? 'No cart available.');
		deleteOutdatedHeaders();
		return of(null);
	}),

);

/**
 * Emits from cart header updates
 */
const cartExpiresSubject$ = new BehaviorSubject<CartHeader | null>(null);
export const updateCartExpires = (header: CartHeader | null) => cartExpiresSubject$.next(header);

const cartExpires$ = cartExpiresSubject$.pipe(
	switchMap((header) => {
		if (header?.expiresAt) return timer(header.expiresAt - Date.now()).pipe(
			map(() => header),
		);
		return EMPTY;
	}),
	switchMap((header) => deleteAnon$(`ca://restaurant/cart/${header?.id}`)),
	map((_) => {
		toastInfo('Cart expired.', true, 'Your cart is out of date and has been removed.');
		return null;
	}),
);
/**
 * Emits from menu UI
 */
const addItemSubject$: Subject<ItemAndMenu> = new Subject();

/**
 * Each emit checks the restaurant and current cart state
 * Will switch to existing restaurant from cart header or create a new cart if needed
 */
const addItemUpdateError$: Observable<any> = addItemSubject$.pipe(
	withLatestFrom(addressStream),
	switchMap(([itemAndMenu, address]) => {
		
		const cartHeader = getCartHeader(itemAndMenu.restaurant_id, cleanAddress(address) as Address);
		
		if (cartHeader) return getAnon$<Cart>(`ca://restaurant/cart/${cartHeader.id}`).pipe(
			catchError((er) => {
				return createCart(itemAndMenu, { address: cleanAddress(address) } as Customer);
			})
		) as Observable<Cart>;
		return createCart(itemAndMenu, { address: cleanAddress(address) } as Customer);
	}),
	withLatestFrom(addItemSubject$),
	switchMap(([cart, { item, menu_id, restaurant_id, editIndex }]) => {
		if (!cart) return of(null);
		const url = `ca://restaurant/cart/${cart.uuid}/items`;
		const payload = { restaurant_id, menu_id, ...item };
		return typeof editIndex === 'number' ? putAnon$<Cart>(`${url}/${editIndex}`, payload) : postAnon$<Cart>(url, payload);
	}),
	share(), // FEW-864 - prevents returned promise from emittied again
);

const addItemUpdate$ = addItemUpdateError$.pipe(
	catchError((e) => {
		toast({ type: AlertType.Error, title: e.friendly_message ?? 'Our server is currently unable to add this item.' });
		return of(null);
	}),
	filter((v) => !!v),
);

/**
 * Emits from cart quantity UI
 */
const quantitySubject$: Subject<{ index: number, quantity: number }> = new Subject();

/**
 * Will not emit until server reply,
 * Quantity component handles immediate state change
 */
const quantityUpdate$: Observable<Cart> = merge(
	quantitySubject$.pipe( // server update
		withLatestFrom(cartSubject$),
		tap(([{ index, quantity }, cart]) => {
			const keyh = formatCartHeader(cart.restaurant.id, cart.customer?.address.geo_location);
			const head = getItem(keyh);
			const expiresAt = cart.updated_at_ts + (cart.cart_ttl_seconds * 1000);
			setItem(keyh, { ...head, itemCount: quantity, lastUpdated: Date.now(), expiresAt } as CartHeader);
		}),
		switchMap(([{ index, quantity }, { uuid }]) => {
			if (quantity) return patchAnon$<Cart>(`ca://restaurant/cart/${uuid}/items/${index}`, { quantity }) as Observable<Cart>;
			return deleteAnon$<Cart>(`ca://restaurant/cart/${uuid}/items/${index}`) as Observable<Cart>;
		}),
	),
	quantitySubject$.pipe( // client update, emits after quantity picker debounce
		withLatestFrom(cartSubject$),
		map(([{ quantity, index }, cart]) => {
			cart.items[index].quantity = quantity;
			return cart;
		}),
	),
);

/**
 * Delivery and collect pills
 * New carts are delivery by default if allowed (set from platform)
 */
export const deliverySubject$ = new RxWritable<patchDelivery>({});


const deliveryUpdate$: Observable<Cart> = merge(
	deliverySubject$.pipe(
		filter((update) => !!update?.type),
		withLatestFrom(cartSubject$),
		filter(([update, cart]) => update.type !== cart.type),
		switchMap(([update, { uuid }]) => patchAnon$<Cart>(`ca://restaurant/cart/${uuid}`, update)),
	),
	deliverySubject$.pipe(
		filter((update) => !!update?.order_notes),
		debounceTime(800),
		withLatestFrom(cartSubject$),
		filter(([update, cart]) => update.order_notes !== cart.order_notes),
		switchMap(([update, cart]) => patchAnon$<Cart>(`ca://restaurant/cart/${cart.uuid}`, update)),
	),
);

/**
 * TODO:
 * This is unused, but will be useful if we need a clear cart button
 * Might need to delete cart once an order is created?
 */
const deleteSubject$ = new Subject<null>();

/**
 * Null = no cart
 */
const deleteUpdate$ = deleteSubject$.pipe(
	withLatestFrom(cartSubject$),
	tap(([, { restaurant, customer }]) => {
		const keyh = formatCartHeader(restaurant.id, customer.address.geo_location);
		setItem(keyh); // delete local copy
	}),
	switchMap(([, { uuid }]) => deleteAnon$(`ca://restaurant/cart/${uuid}`)),
	map((_) => null),
);

// const update = (cart: Cart) => {
// 	cartSubject$.next(cart); // subject
// 	updateCartHeader(cart); // header
// };

/**
 * Exported cart stream
 */
export const cart$: Observable<Cart> = merge(
	initCart$,
	addItemUpdate$,
	quantityUpdate$,
	deliveryUpdate$,
	deleteUpdate$, // only null
	cartExpires$ // only null
).pipe(
	tap((cart) => {
		cartSubject$.next(cart);
		updateCartHeader(cart);
		if (cart) editDelivery({
			type: cart.type,
			order_notes: cart.order_notes
		});
	}),
	// share(), // we really want the `shareReplay(1)` so that cart edit does not miss the last emit, but it is failing a few unit tests
	shareReplay(1), // food item edit was missing the last emit with `share()` #SHAREISSUE
	tap((cart) => validate(cart)), // FEW-966 - validate must happen on every emit
);

// create hot

function createCart({ item, menu_id, restaurant_id }: ItemAndMenu, customer: Customer): Observable<Cart | null> {
	return postAnon$<Cart>(`ca://restaurant/cart`, {
		menu_id,
		restaurant: { id: restaurant_id, },
		customer,
		// items: [item]
	})
	// Dan: removed according to FEW-752
	// .pipe(
	// 	map((cart) => {
	// 		if (_cartIsValid(cart)) return cart;
	// 		toast({
	// 			type: AlertType.Info,
	// 			title: 'Order in progress',
	// 			message: `You currently have an order in progress for ${cart.restaurant.name}.`,
	// 		}, true);
	// 		return null;
	// 	})
	// );
}

export function checkout(uuid: string) {
	return post$<Order>(`ca://restaurant/cart/${uuid}/checkout`, {}, true);
}

/**
 * A cart-id independant method to get item prices base on a customer's selection of
 * optimisoptimisons, extras and addons.
 * @param cart The customer's selection structured as a cart.
 * @returns A stream of the server's response.
 */
export function getQuote(cart:Pick<CartPayload, 'restaurant' | 'menu_id' | 'items'>) {
	return addressStream.pipe(
		take(1),
		map(address => ({
			...cart,
			customer: {
				address: cleanAddress(address)
			},
		})),
		// filter(({ customer }) => !!customer.address && hasGeo(customer.address)),
		switchMap(payload => {
			if (!payload.customer?.address || !hasGeo(payload.customer.address)) return of(null);
			return post$<Quote>(`ca://restaurant/cart?quote=true`, payload)
		}),
		share(),
	);
}

/**
 * @deprecated Use exported cart$ directly
 */
export const getActiveCart = () => cart$;

/**
 * Since the 'add' button will never be disabled
 * this function is now the torch bearer of item validity!
 * Handle with care <3
 * @param update
 * @returns A promise containing the updated cart
*/
export const addItem = (update: ItemAndMenu) => {
	const promise = firstValueFrom(addItemUpdateError$);
	addItemSubject$.next(update);
	return promise;
};

export const editDelivery = (update: patchDelivery) => {
	deliverySubject$.update((current) => ({
		...current,
		...(update.type && { type: update.type }),
		order_notes: update.order_notes,
	}));
};

/**
 * @param index The selected index from the cart's list of items.
 * @param quantity Desired quantity - zero will remove the item from cart.
 * @returns The updated cart from the server.
 */
export const editItemQuantity = (index: number, quantity: number) => quantitySubject$.next({ index, quantity });

export function deleteCart() {
	deleteSubject$.next(null);
}

/**
 * Adds 
 */
export const cartItemQuantity$ = cart$.pipe(
	map((cart) => cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0),
);

//#############################################################
// Cart headers (local storage)
//#############################################################

/**
 * Ensures cart headers are the same
 */
function formatCartHeader(restaurant_id: number, geo_location?: GeoPoint) {
	if (!geo_location) return '';
	return`${KEY_HEADER}-${restaurant_id}-${geo_location.lat}-${geo_location.lon}`;
}

/**
 * Should be used on page load
 * @returns Cart header with latest lastUpdated AND current address
 */
function getLatestCartHeader(address: LandingItemsLocation) {
	if (!address) return null;

	const headers = allKeysStartingWith(KEY_HEADER).map((key) => getItem<CartHeader>(key));
	return headers
		.filter((h) =>
			h.vertical &&
			h.vertical === (address.vertical ?? Vertical.Restaurant) &&
			h.address && // prevents old headers without address
			h.address.geo_location?.lat === +(address.latitude || 0) &&
			h.address.geo_location?.lon === +(address.longitude || 0)
		)
		.reduce((acc, current) =>
			(current?.lastUpdated ?? 0) > (acc?.lastUpdated ?? 0) ? current : acc, null as CartHeader | null
		);
}

function getCartHeader(restaurant_id: number, address: Partial<Address>) {
	return getItem<CartHeader>(formatCartHeader(restaurant_id, address.geo_location));
}

async function updateCartHeader(cart: Cart) {
	if (!cart) return;
	
	const address = await firstValueFrom(addressStream);
	if (!address) {
		console.warn('You must already have an address set to save cart header.');
		return;
	}
	const vertical = address?.vertical || Vertical.Restaurant;
	const keyh = formatCartHeader(cart.restaurant.id, cart.customer?.address.geo_location);

	setItem(keyh); // delete old
	const header = setItem(keyh, {
		vertical,
		restaurantId: cart.restaurant.id,
		id: cart.uuid,
		address: cart.customer.address,
		itemCount: cart.items?.reduce((total, item) => total + item.quantity, 0) ?? 0,
		expiresAt: cart.updated_at_ts + (cart.cart_ttl_seconds * 1000),
		lastUpdated: Date.now(),
		checked_out: cart.status === 'checked_out',
	});

	/**
	 * Sets a timer to delete SS cart after TTL
	 */
	updateCartExpires(header as CartHeader);
}

export function deleteHeadersByRestaurant(restaurant_id: number) {
	allKeysStartingWith(`${KEY_HEADER}-${restaurant_id}`).forEach((key) => {
		const header = getItem(key);
		firstValueFrom(deleteAnon$(`ca://restaurant/cart/${header.id}`));
		setItem(key);
	});
}

function deleteOutdatedHeaders() {
	return allKeysStartingWith(KEY_HEADER).filter((key) => {
		const invalid = (getItem<CartHeader>(key).expiresAt || 0) < Date.now();
		if (invalid) setItem(key);
		return !invalid;
	});
}

export function watchCartHeader(restaurantId: number | string, setIfNotSet = false) {
	if (!restaurantId) return EMPTY;
	const keyh = formatCartHeader(+restaurantId, addressStream.value.geo_location);
	return observe<CartHeader>(keyh).pipe(map(si => si.value));
}

//#############################################################
// Can checkout validation
//#############################################################
const _suite = create((cart: Cart) => {
	mode(Modes.ONE);
	
	test('can_check_out', () => {
		enforce(cart.can_check_out)
			.message(capitalizeFirstLetter(cart.check_out_error) ?? 'Please amend your cart to checkout.')
			.isTruthy();
	});

	test('has_items', () => {
		enforce(cart.items.length)
			.message('Please add an item.')
			.greaterThan(0);
	});

	omitWhen(cart.type === DeliveryOption.Collect, () => {
		each(cart.items, (item) => {

			test((item.item_id ?? 0).toString(), `Please remove collect only item '${item.item_name}'.`, () => {
				enforce(item.collect_only)
					.notEquals(true);
			});

		});
	});

});

export function validate(cart: Cart | null) {
	if (cart) {
		_suite(cart);
	} else {
		_suite.reset();
	}
}

export const suite$ = fromStore(_suite).pipe(
	map(() => _suite.get()),
);

//#############################################################
// Cart Selection Cleanup
//#############################################################

export function isolateCartItemSelection(item: ValueFromArray<Cart['items']>): CartItem {
	if (!item) return item;
	const out: CartItem & {maxQuantity?:number} = {
		section_id: item.section_id,
		item_id: item.item_id,
		variant_id: item.variant_id,
		quantity: item.quantity,
		options: isolateChoice(item.options),
		extras: isolateChoice(item.extras),
		addons: isolateChoice(item.addons),
		maxQuantity: item.maximum_select
	};
	return out;
}

function isolateChoice<T extends ChoiceGroup | ChoiceGroup<WithOptionsAndExtras>>(list: T[]) {
	return list?.map(c => ({
		id: c.id,
		items: c.items?.map(i => {
			const out: IDed & {options?: ChoiceGroup[], extras?: ChoiceGroup[]} = {
				id: i.id,
				quantity: i.quantity,
			}
			// in case of addons
			if ('options' in i && i.options) out.options = isolateChoice(i.options);
			if ('extras' in i && i.extras) out.extras = isolateChoice(i.extras);
			return out;
		})
	}))
}


// ############################################################
// MENU
// ############################################################

export function checkForModifierPrice(item:Item, options: Menu['options'], extras: Menu['extras'], addons: Menu['addons']){
	const lowestPricesOverall:number[] = []; //each variant will have a lowest possible price, each will be represented at an index
	const highestPricesOverall:number[] = [];

	let lowest_variant_price = Number.MAX_SAFE_INTEGER;
	let lowest_variant_old_price = 0;

	if (item.variants) item.variants.forEach(variant => {
		const lowestPrices:number[] = []; //all the lowest prices of a variant
		const highestPrices:number[] = [];

		if (variant.old_price) {
			if (variant.price < lowest_variant_price) {
				lowest_variant_price = variant.price;
				lowest_variant_old_price = variant.old_price;
			}
		}

		const [min, max] = getOptionExtraMinMax(variant, options, extras);
		lowestPrices.push(min);
		highestPrices.push(max);

		// ADD-ONS
		const listAddons = variant.addon_indices?.map(idx => addons[idx]).filter(a => !!a) as AddonGroup[];
		if (listAddons?.length) listAddons.forEach(addon => {
			if (!addon.price_modifier) { //these are pricesless addon items
				lowestPrices.push(0); //GW: actually sum of all prices >0
				return; //continue
			} else if (!addon.items || !addon.items.length) {
				lowestPrices.push(0);
				return; //continue
			}

			const addonItems = addon.items.map(item => {
				const [min, max] = getOptionExtraMinMax(item, options, extras);
				return { price: item.price, plus: { min, max }};
			});

			/**
			 * WRT the following (*)NB comments, the following addon min/max calculations
			 * have a big flaw. It is theoretcially possible to require a customer to select
			 * more than one addon. We only know the true cost of an addon after the selected
			 * addons are combined and the modifier is applied. Given that each addon may then
			 * have varying options and extras this could tip the scales making another set of
			 * addons cost less/more. At best we could include the pices of options and extras
			 * to the sorting.
			 */

			// get lowest priced addon
			const lowestPricedItems = addonItems.slice()
			.sort((a,b) => (a.price + a.plus.min) - (b.price + b.plus.min)) // (*)NB: include options/extras price
			.splice(0, addon.minimum_select ?? 0);
			const lowestModified = getAddonGroupSubTotal(lowestPricedItems.map(i => i.price), addon.price_modifier, variant.price);
			const lowestOptionsExtras = lowestPricedItems.reduce((acc,i) => acc + i.plus.min, 0);
			lowestPrices.push(lowestModified + lowestOptionsExtras);
			
			// get highest priced addon
			const highestPricedItems = addonItems.slice()
			.sort((b,a) => (a.price + a.plus.min) - (b.price + b.plus.min)) // (*)NB: include options/extras price
			.splice(0, addon.maximum_select ?? 0);
			const highestModified = getAddonGroupSubTotal(highestPricedItems.map(i => i.price), addon.price_modifier, variant.price);
			const highestOptionsExtras = highestPricedItems.reduce((acc,i) => acc + i.plus.max, 0)
			highestPrices.push(highestModified + highestOptionsExtras);
		});

		//get the sum of required prices
		lowestPricesOverall.push(variant.price + (lowestPrices.length? lowestPrices.reduce((a,v) => a+v, 0) : 0));
		highestPricesOverall.push(variant.price + (highestPrices.length? highestPrices.reduce((a,v) => a+v, 0) : 0));
	});

	//return the lowest variant price as lowest item price
	const low = lowestPricesOverall.reduce((a,v) => a>v?v:a, lowestPricesOverall[0]);
	const high = highestPricesOverall.reduce((a,v) => a>v?a:v, highestPricesOverall[0]);
	//console.log('PRICE', item.name, low, high);

	const itemPriced: {
		showFrom: boolean;
		price: number;
		oldPrice?: number;
	} = {
		price: low,
		showFrom: low !== high, // `from` caption above item price
	};
	if (lowest_variant_old_price) {
		itemPriced.showFrom = itemPriced.showFrom && (lowest_variant_price === low); // show from if lowest menu deal is equals lowest price
		itemPriced.oldPrice = lowest_variant_old_price;
		itemPriced.price = lowest_variant_price;
	}
	return itemPriced;
}

function getOptionExtraMinMax(variant: { extra_indices?: number[], option_indices?: number[] }, options: Menu['options'], extras: Menu['extras']):[number,number] {
	const lowestPrices = [] as number[];
	const highestPrices = [] as number[];
	let lows: number[], highs: number[];

	// EXTRAS
	const listExtras = variant.extra_indices?.map(idx => extras[idx]).filter(item => !!item) as ExtraGroup[];
	if (listExtras) {
		[lows, highs] = getHiLows(listExtras);
		lowestPrices.push(...lows);
		highestPrices.push(...highs);
	}

	// OPTIONS
	const listOptions = variant.option_indices?.map(idx => options[idx]).filter(item => !!item) as OptionGroup[];
	if (listOptions) {
		[lows, highs] = getHiLows(listOptions);
		lowestPrices.push(...lows);
		highestPrices.push(...highs);
	}

	return [
		lowestPrices.reduce((a,v) => a+v, 0),
		highestPrices.reduce((a,v) => a+v, 0),
	];
}

function getHiLows(list: {items:{ price: number }[], minimum_select?: number, maximum_select?: number }[]):[number[],number[]] {
	const lows:number[] = [];
	const highs:number[] = [];
	list.forEach(item => {
		if (!item.items.length) return;

		const low = item.items.slice() // clone
		.sort((a,b) => (a.price ?? 0) - (b.price ?? 0)) // sort low to high
		.splice(0, item.minimum_select ?? 0) // splice minimum low prices
		.map(i => i.price) // pluck price
		.reduce((a,v) => a + (v ?? 0), 0); // sum
		lows.push(low);

		const high = item.items.slice() // clone
		.sort((b,a) => (a.price ?? 0) - (b.price ?? 0)) // sort high to low
		.splice(0,item.maximum_select ?? 0) // splice maximum high prices
		.map(i => i.price) // pluck price
		.reduce((a,v) => a + (v ?? 0), 0); // sum
		highs.push(high);
	});
	return [lows, highs];
}

export function getAddonGroupSubTotal(prices:number[], mod:string, delta:number) {
	if (!prices || !prices.length) return 0;
	if (!delta) delta = 0;
	if (!mod) {
		prices = prices.map(p => p ?? 0);
		mod = 'SUM_PRICES';
	}
	switch (mod) {
		case 'HIGHEST_PRICE':
			return prices.reduce((a,v) => v > a ? v : a, 0);
		case 'LOWEST_PRICE':
			return prices.reduce((a,v) => v < a ? v : a, prices[0]);
		case 'SUM_PRICES':
			return prices.reduce((a,v) => a + v, 0);
		case 'DELTA_PRICES':
			return prices.map(p => Math.max(0, p - delta)).reduce((a,v) => a+v,0);
	
		default:
			console.warn('Cannot calculate addons with price modifier:', mod);
			break;
	}
	return 0;
}

export function isFreeDelivery(label: string): boolean {
	return label.toLowerCase().includes('free');
}
